import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  FilterCard,
  SummaryCard,
  ListHeaderRow,
  Fab,
  StatusPill,
  CardKV,
  BORDER,
  MUTED,
  TEXT,
} from "../../components/requestUi";
import {
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DesktopRequestFilters,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { getMakeupWorkListApi, deleteMakeupWorkApi } from "../../api/requests.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";

const EditIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 20h9" stroke="#2E90FA" strokeWidth="2" strokeLinecap="round" />
    <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="#2E90FA" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const MakeupList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [fromDate, setFromDate] = useState(moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["emp", "makeup-list", status, fromDate, toDate, sortOrder],
    queryFn: () =>
      getMakeupWorkListApi({
        page: 1,
        limit: 20,
        sortBy: "createdAt",
        sortOrder,
        startTime: fromDate,
        endTime: toDate,
        ...(status !== "ALL" ? { status } : {}),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteMakeupWorkApi(id),
    onSuccess: () => {
      toast.success("Đã xóa đăng ký làm bù");
      queryClient.invalidateQueries({ queryKey: ["emp", "makeup-list"] });
      setDeleteId(null);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e, "Xóa đơn thất bại"));
      setDeleteId(null);
    },
  });

  const items = data?.items || [];
  const total = data?.pagination?.total ?? data?.total ?? items.length;

  const confirmDialog = (
    <ConfirmDialog
      isOpen={!!deleteId}
      title="Xóa đơn làm bù"
      description="Bạn chắc chắn muốn xóa đơn làm bù này?"
      confirmText="Xóa"
      cancelText="Hủy"
      danger
      loading={deleteMutation.isPending}
      onConfirm={() => deleteMutation.mutate(deleteId)}
      onClose={() => setDeleteId(null)}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((item) => ({
      id: item._id,
      raw: item,
      createdAt: item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "-",
      dayLeave: item.dayLeave ? moment(item.dayLeave).format("DD/MM/YYYY") : "-",
      note: item.note || "-",
      isPending: String(item.status || "").toUpperCase() === "PENDING",
    }));

    const openDetail = (row) => navigate(`${EMP_BASE_PATH}/lam-bu/${row.id}`);

    return (
      <DesktopPage
        title="Đăng ký làm bù"
        right={
          <DesktopButton onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu/tao`)}>
            + Đăng ký làm bù
          </DesktopButton>
        }
      >
        <DesktopStatRow items={[{ key: "total", title: "Tổng số đơn", total: total || 0 }]} />

        <DesktopPanel p="0">
          <DesktopRequestFilters
            status={status}
            onStatusChange={setStatus}
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
          />

          <DesktopTable
            loading={isLoading}
            emptyText="Không có đơn làm bù"
            onRowClick={openDetail}
            rows={rows}
            headers={[
              { key: "createdAt", label: "Ngày tạo", width: "16%" },
              { key: "dayLeave", label: "Ngày làm bù", width: "16%" },
              { key: "note", label: "Nội dung", align: "left", width: "38%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "14%",
                render: (row) => <StatusPill status={row.raw.status} />,
              },
              {
                key: "actions",
                label: "Thao tác",
                width: "16%",
                render: (row) =>
                  row.isPending ? (
                    <Flex justify="center" gap="8px" onClick={(e) => e.stopPropagation()}>
                      <Flex
                        as="button"
                        w="32px"
                        h="32px"
                        borderRadius="8px"
                        bg="#FFF7E6"
                        align="center"
                        justify="center"
                        onClick={() => openDetail(row)}
                        title="Cập nhật"
                      >
                        <Box as={FiEdit} color="#F79D0D" fontSize="16px" />
                      </Flex>
                      <Flex
                        as="button"
                        w="32px"
                        h="32px"
                        borderRadius="8px"
                        bg="#FFF0F0"
                        align="center"
                        justify="center"
                        onClick={() => setDeleteId(row.id)}
                        title="Xóa"
                      >
                        <Box as={FiTrash2} color="#E5484D" fontSize="16px" />
                      </Flex>
                    </Flex>
                  ) : (
                    "-"
                  ),
              },
            ]}
          />
        </DesktopPanel>

        {confirmDialog}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Danh sách đăng ký làm bù" onBack={() => navigate(EMP_BASE_PATH)} />
      <Box px="16px" pt="12px">
      <FilterCard
        open={filterOpen}
        onToggle={() => setFilterOpen((v) => !v)}
        status={status}
        onStatusChange={setStatus}
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
      />

      <SummaryCard label="TỔNG SỐ ĐƠN" total={total} />

      <ListHeaderRow
        title="Danh sách đơn làm bù"
        sortLabel={sortOrder === "desc" ? "Mới đến cũ" : "Cũ đến mới"}
        onToggleSort={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
      />

      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
          Không có đơn làm bù
        </Text>
      ) : (
        <Flex direction="column" gap="10px">
          {items.map((item) => {
            const isPending = String(item.status || "").toUpperCase() === "PENDING";
            return (
              <Box key={item._id} bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
                <Flex align="center" justify="space-between">
                  <Flex
                    as="button"
                    align="center"
                    gap="8px"
                    flexWrap="wrap"
                    flex="1"
                    onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu/${item._id}`)}
                  >
                    <Text fontSize="14px" color={TEXT}>
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </Text>
                    <StatusPill status={item.status} />
                  </Flex>

                  {isPending && (
                    <Menu placement="bottom-end">
                      <MenuButton
                        as="button"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Thao tác"
                      >
                        <Flex w="44px" h="44px" borderRadius="8px" bg="#EFF6FF" align="center" justify="center">
                          <EditIcon />
                        </Flex>
                      </MenuButton>
                      <MenuList minW="180px" borderRadius="12px">
                        <MenuItem
                          icon={<Box as={FiEdit} color="#F79D0D" fontSize="18px" />}
                          onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu/${item._id}`)}
                        >
                          Cập nhật
                        </MenuItem>
                        <MenuItem
                          icon={<Box as={FiTrash2} color="#E5484D" fontSize="18px" />}
                          color="#E5484D"
                          onClick={() => setDeleteId(item._id)}
                        >
                          Xóa
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </Flex>

                <Box h="1px" bg={BORDER} my="12px" />
                <Box as="button" w="100%" textAlign="left" onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu/${item._id}`)}>
                  <CardKV label="Ngày làm bù" value={item.dayLeave ? moment(item.dayLeave).format("DD/MM/YYYY") : "-"} />
                  <Box h="8px" />
                  <CardKV label="Nội dung" value={item.note || "-"} />
                </Box>
              </Box>
            );
          })}
        </Flex>
      )}
      </Box>

      <Fab onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu/tao`)} />

      {confirmDialog}
    </Box>
  );
};

export default MakeupList;
